// The survey builder's state (KODIF-4488): the questions as the merchant edits them, in order, and the one they're
// working on. It renders the questions as the nested form fields Ai::SurveysController saves (formFields) and as the
// survey the chat widget shows (widgetSurvey), so the live preview shows unsaved edits.

let nextUid = 0

// Question types, in the order the builder offers them.
export const QUESTION_TYPES = [
  { name: 'rating', label: 'Star rating', hint: '1 to 5 stars', labelExamples: ['Very poor', 'Excellent'] },
  {
    name: 'scale', label: 'Scale', hint: 'A numbered range, like effort from 1 to 7',
    labelExamples: ['Very hard', 'Very easy'],
  },
  { name: 'yes_no', label: 'Yes / No', hint: 'Like "Was your issue resolved?"' },
  { name: 'choice', label: 'Multiple choice', hint: 'Pick one of your options' },
  { name: 'text', label: 'Free text', hint: 'Anything they want to say' },
]

export const OPERATOR_LABELS = { lte: 'is at most', gte: 'is at least', equals: 'is', in: 'is one of' }

const RATING_RANGE = { min: 1, max: 5 }

// Examples of what a type's lowest and highest answers could be labelled; a type without them takes no labels.
export function labelExamples(type) {
  return QUESTION_TYPES.find(questionType => questionType.name === type)?.labelExamples
}

export function slug(text) {
  return text.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '').slice(0, 40).replace(/_+$/, '')
}

export class SurveyBuilder {
  questions = $state([])
  removedIds = $state([])
  selectedUid = $state(null)

  // questions: the survey's questions as the form has them (SurveysHelper#survey_builder_questions); removedIds: saved
  // ones already removed (survey_builder_removed_ids); operators: the follow-up operators each source type takes
  // (SurveyQuestionType.condition_operators).
  constructor({ questions, removedIds = [], operators }) {
    this.operators = operators
    this.removedIds = removedIds
    const drafts = questions.map(question => this.#draft(question))
    const uidsByKey = Object.fromEntries(drafts.map(draft => [draft.key, draft.uid]))
    drafts.forEach((draft, index) => {
      const condition = questions[index].condition
      if (condition?.question_key) {
        draft.condition = { sourceUid: uidsByKey[condition.question_key] ?? null, operator: condition.operator,
                            value: condition.value }
      }
    })
    this.questions = drafts
    this.selectedUid = drafts[0]?.uid ?? null
  }

  get selected() {
    return this.questions.find(question => question.uid === this.selectedUid) ?? null
  }

  add(type) {
    const draft = this.#draft({ question_type: type, prompt: '', required: false })
    this.questions.push(draft)
    this.selectedUid = draft.uid
    // The reactive copy the list now holds, not the plain draft.
    return this.questions.at(-1)
  }

  // What removing the question will do, for the merchant to confirm.
  removalWarning(question) {
    const followUps = this.questions.filter(other => other.condition?.sourceUid === question.uid)
    return [
      `Remove "${this.prompt(question)}"?`,
      question.id && 'Answers customers already gave stay in reports.',
      followUps.length && `${followUps.length === 1 ? 'Its follow-up' : `Its ${followUps.length} follow-ups`} will need another question to depend on.`,
      'Nothing changes until you save the survey.',
    ].filter(Boolean).join(' ')
  }

  remove(question) {
    if (question.id) this.removedIds.push(question.id)
    this.questions = this.questions.filter(other => other !== question)
    if (this.selectedUid === question.uid) this.selectedUid = this.questions[0]?.uid ?? null
  }

  move(question, toIndex) {
    const from = this.questions.indexOf(question)
    if (from < 0 || toIndex < 0 || toIndex >= this.questions.length || from === toIndex) return
    const questions = [...this.questions]
    questions.splice(from, 1)
    questions.splice(toIndex, 0, question)
    this.questions = questions
  }

  setType(question, type) {
    if (question.type === type) return

    question.type = type
    if (type === 'scale' && question.min === undefined) Object.assign(question, { min: 1, max: 5 })
    if (type === 'choice' && question.values.length < 2) question.values = [...question.values, '', ''].slice(0, 2)
    // Follow-ups on this question were written for its old answers.
    this.questions.filter(other => other.condition?.sourceUid === question.uid).forEach(other => {
      other.condition = this.defaultCondition(question)
    })
  }

  // The questions a follow-up can depend on: earlier ones whose answers can be compared (not free text).
  sourcesFor(question) {
    const index = this.questions.indexOf(question)
    return this.questions.slice(0, index).filter(source => this.operators[source.type]?.length)
  }

  source(question) {
    return this.questions.find(other => other.uid === question.condition?.sourceUid) ?? null
  }

  // Why a follow-up's condition can't be saved as it is, or null.
  conditionProblem(question) {
    if (!question.condition) return null
    const source = this.source(question)
    if (!source) return 'Pick the question this follow-up depends on.'
    if (this.questions.indexOf(source) > this.questions.indexOf(question)) {
      return `"${this.prompt(source)}" now comes after this question. Move it back above, or pick another question.`
    }
    if (source.type === 'choice' && !(question.condition.value ?? []).length) return 'Pick at least one choice.'
    return null
  }

  defaultCondition(source) {
    const operator = this.operators[source.type]?.[0]
    if (!operator) return null
    const value = {
      rating: 3,
      scale: Math.floor(((source.min ?? 1) + (source.max ?? 5)) / 2),
      yes_no: false,
      choice: this.choices(source).slice(0, 1),
    }[source.type]
    return { sourceUid: source.uid, operator, value }
  }

  // The answers a source question takes, for a follow-up's value picker.
  answerChoices(source) {
    if (source.type === 'yes_no') return [{ value: true, label: 'Yes' }, { value: false, label: 'No' }]
    if (source.type === 'choice') return this.choices(source).map(value => ({ value, label: value }))
    const { min, max } = source.type === 'rating' ? RATING_RANGE : { min: source.min ?? 1, max: source.max ?? 5 }
    return Array.from({ length: Math.max(0, max - min + 1) }, (_unused, index) => {
      const value = min + index
      return { value, label: source.type === 'rating' ? `${value} ★` : String(value) }
    })
  }

  // How a follow-up's operator reads for its source ("is one of" a single choice, "includes one of" several picks).
  operatorLabel(source, operator) {
    return operator === 'in' && source?.multiple ? 'includes one of' : OPERATOR_LABELS[operator]
  }

  // A choice question's choices as saving keeps them: filled in, each once (SurveyQuestion#choice_values).
  choices(question) {
    return [...new Set(question.values.map(value => value.trim()).filter(Boolean))]
  }

  prompt(question) {
    return question.prompt.trim() || 'Untitled question'
  }

  // Each question's reporting key: the one it was saved or typed with, else derived from its prompt, stepping around
  // keys already taken (as Survey#assign_question_keys_and_positions does). Derived once per edit.
  keys = $derived.by(() => {
    const keys = new Map()
    const taken = new Set(this.questions.filter(question => question.keyTyped).map(question => question.key))
    this.questions.forEach((question, index) => {
      if (question.keyTyped) return keys.set(question.uid, question.key)

      const base = slug(question.prompt) || `question_${index + 1}`
      let key = base
      for (let suffix = 2; taken.has(key); suffix += 1) key = `${base}_${suffix}`
      taken.add(key)
      keys.set(question.uid, key)
    })
    return keys
  })

  // The survey as the chat widget reads it (Shipped::V1::Entities::Survey), for the live preview.
  widgetSurvey() {
    const keys = this.keys
    return {
      questions: this.questions.map(question => {
        const payload = { key: keys.get(question.uid), prompt: this.prompt(question), type: question.type,
                          required: question.required }
        if (question.type === 'rating') Object.assign(payload, RATING_RANGE)
        if (question.type === 'scale') Object.assign(payload, { min: Number(question.min), max: Number(question.max) })
        if (labelExamples(question.type)) {
          Object.assign(payload, { min_label: question.minLabel || undefined, max_label: question.maxLabel || undefined })
        }
        if (question.type === 'choice') {
          payload.values = this.choices(question)
          if (question.multiple) payload.multiple = true
        }
        const source = this.source(question)
        if (source && !this.conditionProblem(question)) {
          payload.condition = { question_key: keys.get(source.uid), operator: question.condition.operator,
                                value: question.condition.value }
        }
        return payload
      }),
    }
  }

  // The nested attributes Ai::SurveysController#survey_params takes, as [name, value] pairs.
  formFields(prefix) {
    const keys = this.keys
    const fields = []
    this.questions.forEach((question, index) => {
      const name = attribute => `${prefix}[${index}][${attribute}]`
      if (question.id) fields.push([name('id'), question.id])
      fields.push([name('key'), keys.get(question.uid)], [name('prompt'), question.prompt],
                  [name('question_type'), question.type], [name('required'), question.required ? '1' : '0'],
                  [name('position'), index])
      if (question.type === 'scale') fields.push([name('min'), question.min], [name('max'), question.max])
      if (labelExamples(question.type)) {
        fields.push([name('min_label'), question.minLabel], [name('max_label'), question.maxLabel])
      }
      if (question.type === 'choice') {
        const values = this.choices(question)
        fields.push(...(values.length ? values : ['']).map(value => [`${name('values')}[]`, value]),
                    [name('multiple'), question.multiple ? '1' : '0'])
      }
      fields.push(...this.#conditionFields(question, keys, attribute => name(`condition][${attribute}`)))
    })
    this.removedIds.forEach((id, index) => {
      const name = attribute => `${prefix}[removed_${index}][${attribute}]`
      fields.push([name('id'), id], [name('_destroy'), '1'])
    })
    return fields
  }

  #conditionFields(question, keys, name) {
    const source = this.source(question)
    if (!source) return [[name('question_key'), '']]

    const { operator, value } = question.condition
    const values = Array.isArray(value) ? value.map(item => [`${name('value')}[]`, item]) : [[name('value'), value]]
    return [[name('question_key'), keys.get(source.uid)], [name('operator'), operator], ...values]
  }

  #draft(question) {
    nextUid += 1
    return {
      uid: nextUid,
      id: question.id ?? null,
      // A saved question keeps its key (reports group by it); a new one follows its prompt until a key is typed.
      key: question.key ?? '',
      keyTyped: Boolean(question.key),
      prompt: question.prompt ?? '',
      type: question.question_type,
      required: Boolean(question.required),
      min: question.min ?? (question.question_type === 'scale' ? 1 : undefined),
      max: question.max ?? (question.question_type === 'scale' ? 5 : undefined),
      minLabel: question.min_label ?? '',
      maxLabel: question.max_label ?? '',
      values: question.values ?? [],
      multiple: Boolean(question.multiple),
      condition: null,
    }
  }
}
